import React, { useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ProgramInfoForm.module.css";
import { InputBox, ValidatedEmailInput } from "@cpnw/ui";

export const ProgramInfoForm = forwardRef(({ programInfoFormData, onChange }, ref) => {
  const [errors, setErrors] = useState({
    programName: "",
    degreeAward: "",
    streetAddress: "",
    cityStateZip: "",
    primaryName: "",
    primaryEmail: "",
    backupName: "",
    backupEmail: "",
  });

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors = {};

      Object.entries(programInfoFormData).forEach(([key, value]) => {
        const formattedKey = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (char) => char.toUpperCase());

        // Required field check
        if (!value?.trim()) {
          newErrors[key] = `${formattedKey} is required.`;
          return;
        }

        // Email validation
        if (key.toLowerCase().includes("email")) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value.trim())) {
            newErrors[key] = `${formattedKey} must be a valid email.`;
          }
        }
      });

      setErrors(newErrors); // overwrite previous errors

      return Object.keys(newErrors).length === 0;
    }
  }));


  const handleBlur = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    if (!value?.trim()) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${field.charAt(0).toUpperCase() +
          field.slice(1).replace(/([A-Z])/g, " $1")
          } is required`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    const newData = {
      ...programInfoFormData,
      [field]: value,
    };
    onChange(newData);
    if (value?.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div>
      <p className={classNames(styles.message)}>**All fields are required.</p>

      <div className={classNames(styles.container)}>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"Educational Program Name"}
            value={programInfoFormData.programName || ""}
            // required={true}
            onChange={handleChange("programName")}
            onBlur={handleBlur("programName")}
            aria-invalid={!!errors.programName}
            aria-describedby={
              errors.programName ? "programName-error" : undefined
            }
          />
          <p id="programName-error" className={styles.errorMessage}>
            {errors.programName}
          </p>
        </div>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"Degree Award"}
            value={programInfoFormData.degreeAward || ""}
            // required={true}
            onChange={handleChange("degreeAward")}
            onBlur={handleBlur("degreeAward")}
            aria-invalid={!!errors.degreeAward}
            aria-describedby={
              errors.degreeAward ? "degreeAward-error" : undefined
            }
          />
          <p id="degreeAward-error" className={styles.errorMessage}>
            {errors.degreeAward}
          </p>
        </div>
      </div>
      <div className={classNames(styles.container)}>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"Program Street Address"}
            value={programInfoFormData.streetAddress || ""}
            // required={true}
            onChange={handleChange("streetAddress")}
            onBlur={handleBlur("streetAddress")}
            aria-invalid={!!errors.streetAddress}
            aria-describedby={
              errors.streetAddress ? "streetAddress-error" : undefined
            }
          />
          <p id="streetAddress-error" className={styles.errorMessage}>
            {errors.streetAddress}
          </p>
        </div>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"City, State, Zip Code"}
            value={programInfoFormData.cityStateZip || ""}
            // required={true}
            onChange={handleChange("cityStateZip")}
            onBlur={handleBlur("cityStateZip")}
            aria-invalid={!!errors.cityStateZip}
            aria-describedby={
              errors.cityStateZip ? "cityStateZip-error" : undefined
            }
          />
          <p id="cityStateZip-error" className={styles.errorMessage}>
            {errors.cityStateZip}
          </p>
        </div>
      </div>
      <div className={classNames(styles.container)}>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"Primary Coordinator Name"}
            value={programInfoFormData.primaryName || ""}
            // required={true}
            onChange={handleChange("primaryName")}
            onBlur={handleBlur("primaryName")}
            aria-invalid={!!errors.primaryName}
            aria-describedby={
              errors.primaryName ? "primaryName-error" : undefined
            }
          />
          <p id="primaryName-error" className={styles.errorMessage}>
            {errors.primaryName}
          </p>
        </div>
        <div className={classNames(styles.column)}>
          <ValidatedEmailInput
            className={styles.inputBoxRound}
            placeholder={"Primary Coordinator Email"}
            value={programInfoFormData.primaryEmail || ""}
            // required={true}
            onChange={handleChange("primaryEmail")}
          // onChange={(val) => {
          //   const newData = {
          //     ...programInfoFormData,
          //     primaryEmail: val,
          //   };
          //   if (newData.programName?.trim()) {
          //     onChange(newData);
          //   }
          // }}
          />
          <p id="primaryEmail-error" className={styles.errorMessage}>
            {errors.primaryEmail}
          </p>
        </div>
      </div>
      <div className={classNames(styles.container)}>
        <div className={classNames(styles.column)}>
          <InputBox
            className={styles.inputBoxRound}
            placeholder={"Backup Coordinator Name"}
            value={programInfoFormData.backupName || ""}
            // required={true}
            onChange={handleChange("backupName")}
            onBlur={handleBlur("backupName")}
            aria-invalid={!!errors.backupName}
            aria-describedby={
              errors.backupName ? "backupName-error" : undefined
            }
          />
          <p id="backupName-error" className={styles.errorMessage}>
            {errors.backupName}
          </p>
        </div>
        <div className={classNames(styles.column)}>
          <ValidatedEmailInput
            className={styles.inputBoxRound}
            placeholder={"Backup Coordinator Email"}
            value={programInfoFormData.backupEmail || ""}
            // required={true}
            onChange={handleChange("backupEmail")}
          // onChange={(val) => {
          //   const newData = {
          //     ...programInfoFormData,
          //     backupEmail: val,
          //   };
          //   if (newData.programName?.trim()) {
          //     onChange(newData);
          //   }
          // }}
          />
          <p id="backupEmail-error" className={styles.errorMessage}>
            {errors.backupEmail}
          </p>
        </div>
      </div>
    </div>
  );
});

ProgramInfoForm.propTypes = {
  programInfoFormData: PropTypes.shape({
    programName: PropTypes.string,
    degreeAward: PropTypes.string,
    streetAddress: PropTypes.string,
    cityStateZip: PropTypes.string,
    primaryName: PropTypes.string,
    primaryEmail: PropTypes.string,
    backupName: PropTypes.string,
    backupEmail: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
